/* =========================================================
   AF AE Products Slider — cached JS
   File: assets/af-ae-products-slider.js
   Also hosts the shared AFSlider core (see the block below) that the
   homepage journal (af-ae-journal.js) uses.

   Same behavior:
   - Progress rule fill
   - Nav prev/next step scrolling + disabled state
   - Desktop grab/drag + inertia (cards clickable)
   - Mobile swipe + snap

   Perf improvements:
   - Window listeners only while dragging
   - rAF throttled UI updates
   - One ResizeObserver per track
   ========================================================= */
/* ---------------------------------------------------------
   AFSlider core — shared by the products slider (below) and the
   homepage journal (assets/af-ae-journal.js). This is the ONE copy of
   the nav / drag / inertia / snap logic. window.AFSlider.attach(track, o)
   wires one track; the callers keep their own root discovery, init guards
   and timing. o:
     item        selector of a card inside the track (step size)
     prev, next  nav buttons (optional)
     fill        progress-rule fill element (optional)
     cacheMax    true: scroll max cached, recomputed on resize (products
                 slider); false: read live (journal)
     uiOnMql     true: refresh nav/progress when the desktop media query flips
   --------------------------------------------------------- */
(() => {
  "use strict";
  if (window.AFSlider) return;

  const DESKTOP_MQL = window.matchMedia("(hover: hover) and (pointer: fine)");
  const isDesktop = () => DESKTOP_MQL.matches;

  const attach = (track, o) => {
    const prevBtn = o.prev;
    const nextBtn = o.next;
    const fill = o.fill;

    let cachedMax = track.scrollWidth - track.clientWidth;
    const getMax = () => (o.cacheMax ? cachedMax : track.scrollWidth - track.clientWidth);

    const setProgress = () => {
      if (!fill) return;
      const max = getMax();
      const p = max <= 0 ? 0 : Math.max(0, Math.min(1, track.scrollLeft / max));
      fill.style.transform = `scaleX(${p})`;
    };

    const updateNavDisabled = () => {
      if (!prevBtn || !nextBtn) return;
      const max = getMax();
      const x = track.scrollLeft;

      const atStart = x <= 1;
      const atEnd = max <= 1 ? true : x >= max - 1;

      prevBtn.disabled = atStart;
      nextBtn.disabled = atEnd;

      prevBtn.classList.toggle("is-disabled", atStart);
      nextBtn.classList.toggle("is-disabled", atEnd);
    };

    const getStep = () => {
      const card = track.querySelector(o.item);
      if (!card) return Math.round(track.clientWidth * 0.85);

      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || "0") || 0;

      return Math.round(card.getBoundingClientRect().width + gap);
    };

    const scrollByStep = (dir) => {
      track.scrollBy({ left: dir * getStep(), behavior: "smooth" });
    };

    if (prevBtn && nextBtn) {
      prevBtn.addEventListener("click", () => scrollByStep(-1));
      nextBtn.addEventListener("click", () => scrollByStep(1));
    }

    // Throttle UI updates to 1/frame
    let uiRaf = 0;
    const scheduleUI = () => {
      if (uiRaf) return;
      uiRaf = requestAnimationFrame(() => {
        uiRaf = 0;
        setProgress();
        updateNavDisabled();
      });
    };

    track.addEventListener("scroll", scheduleUI, { passive: true });

    const ro = new ResizeObserver(() => {
      if (o.cacheMax) cachedMax = track.scrollWidth - track.clientWidth;
      scheduleUI();
    });
    ro.observe(track);

    // stop native dragging
    track.querySelectorAll("img, a").forEach((el) => el.setAttribute("draggable", "false"));
    track.addEventListener("dragstart", (e) => e.preventDefault(), true);

    // snap policy on desktop
    if (isDesktop()) track.style.scrollSnapType = "none";
    const onMql = () => {
      track.style.scrollSnapType = isDesktop() ? "none" : "";
      if (o.uiOnMql) scheduleUI();
    };
    if (DESKTOP_MQL.addEventListener) DESKTOP_MQL.addEventListener("change", onMql);
    else if (DESKTOP_MQL.addListener) DESKTOP_MQL.addListener(onMql);

    /* Desktop: grab + drag + inertia (with temporary window listeners) */
    let isDown = false;
    let dragged = false;

    let startX = 0;
    let startScrollLeft = 0;

    let dragRaf = 0;
    let targetLeft = 0;

    let lastX = 0;
    let lastT = 0;
    let vx = 0;

    let inertiaRaf = 0;

    const DRAG_THRESHOLD = 6;

    const applyDragScroll = () => {
      dragRaf = 0;
      track.scrollLeft = targetLeft;
    };

    const cancelInertia = () => {
      if (inertiaRaf) {
        cancelAnimationFrame(inertiaRaf);
        inertiaRaf = 0;
      }
    };

    const onMove = (e) => {
      if (!isDown) return;

      const dx = e.clientX - startX;

      if (!dragged && Math.abs(dx) > DRAG_THRESHOLD) {
        dragged = true;
        track.classList.add("is-dragging");
      }

      if (!dragged) return;

      e.preventDefault();

      targetLeft = startScrollLeft - dx;
      if (!dragRaf) dragRaf = requestAnimationFrame(applyDragScroll);

      const now = performance.now();
      const dt = Math.max(1, now - lastT);
      vx = (e.clientX - lastX) / dt;

      lastX = e.clientX;
      lastT = now;
    };

    const startInertia = () => {
      let v = -vx * 12;

      const MAX_V = 1.6;
      v = Math.max(-MAX_V, Math.min(MAX_V, v));

      const friction = 0.86;
      let last = performance.now();

      const step = (t) => {
        const dt = Math.min(32, t - last);
        last = t;

        track.scrollLeft += v * dt;
        v *= Math.pow(friction, dt / 16);

        if (Math.abs(v) > 0.25) {
          inertiaRaf = requestAnimationFrame(step);
        } else {
          inertiaRaf = 0;
          track.classList.remove("is-dragging");
        }
      };

      inertiaRaf = requestAnimationFrame(step);
    };

    const onUp = () => {
      if (!isDown) return;
      isDown = false;

      if (dragRaf) {
        cancelAnimationFrame(dragRaf);
        dragRaf = 0;
      }

      window.removeEventListener("mousemove", onMove, { passive: false });
      window.removeEventListener("mouseup", onUp, true);

      if (dragged) {
        track.addEventListener(
          "click",
          (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
          },
          { capture: true, once: true }
        );
        startInertia();
      } else {
        track.classList.remove("is-dragging");
      }
    };

    const onDown = (e) => {
      if (!isDesktop()) return;
      if (e.button !== 0) return;

      cancelInertia();

      isDown = true;
      dragged = false;

      startX = e.clientX;
      startScrollLeft = track.scrollLeft;

      lastX = e.clientX;
      lastT = performance.now();
      vx = 0;

      window.addEventListener("mousemove", onMove, { passive: false });
      window.addEventListener("mouseup", onUp, true);
    };

    track.addEventListener("mousedown", onDown, true);

    // init
    scheduleUI();
  };

  window.AFSlider = { attach };
  // Scripts that load before this one (lazy sections) wait for this event.
  document.dispatchEvent(new Event("af:slider-core"));
})();

(() => {
  "use strict";

  // Both PDP rails (related-products, af-more-from-vendor) include this file,
  // so it can arrive twice. The first run's init covers every root, so a
  // repeat run has nothing to add (editor section reloads: see the end).
  if (window.__afAePsLoaded) return;
  window.__afAePsLoaded = true;

  const initAll = () => {
    // The core (above, same file) is always defined by now.
    const roots = document.querySelectorAll("[data-af-ae-ps]");
    if (!roots.length) return;

    roots.forEach((root) => {
    if (!root) return;
    if (root.dataset.afAePsInit === "1") return;
    root.dataset.afAePsInit = "1";

    const track = root.querySelector("[data-track]");
    const fill = root.querySelector(".af-ae-ps__rule-fill");
    if (!track || !fill) return;

    window.AFSlider.attach(track, {
      item: ".af-ae-ps__card",
      prev: root.querySelector(".af-ae-ps__nav-btn--prev"),
      next: root.querySelector(".af-ae-ps__nav-btn--next"),
      fill,
      cacheMax: true,
      uiOnMql: true,
    });
    });
  };

  // Defer init until after first paint to avoid blocking FCP
  if ("requestIdleCallback" in window) {
    requestIdleCallback(initAll);
  } else {
    // Safari has no requestIdleCallback; this script can arrive after load
    if (document.readyState === "complete") initAll();
    else window.addEventListener("load", initAll, { once: true });
  }

  // PDP "You may also like": Dawn's <product-recommendations> (global.js)
  // swaps in its innerHTML after an async fetch, and again when a combined
  // listing switches product, so that rail's root arrives after the init
  // above. initAll skips roots it has done, so re-running it on each swap
  // only picks up the new one.
  const watchRecs = (scope) => {
    scope.querySelectorAll("product-recommendations").forEach((el) => {
      new MutationObserver(initAll).observe(el, { childList: true });
    });
  };
  watchRecs(document);

  // Theme editor: a re-rendered section runs this file again, which the
  // load-once guard at the top turns into a no-op, so pick it up here.
  document.addEventListener("shopify:section:load", (e) => {
    watchRecs(e.target);
    initAll();
  });
})();
